import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { scaleOrdinal, schemeCategory20 } from "d3-scale";
import { pie } from "d3-shape";
import Arc from "./Arc.esm.js";
const LABEL_MARGIN = 15;
const ADJUSTMENT_UNIT = 0.175;
function doesIntersect(r1, r2) {
  if (!r1.height || !r1.width || !r2.height || !r2.width) {
    return false;
  }
  return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
}
const getIdentifierFromReactNode = (node) => typeof node === "string" ? node : node.id;
const getRects = (elements) => {
  const rects = {};
  elements.forEach((element) => {
    const key = element.getAttribute("data-key");
    if (!key) {
      throw new Error(`Expected a key while adjusting arc labels but got: ${key}`);
    }
    rects[key] = element.getBoundingClientRect();
  });
  return rects;
};
const getPieArcs = pie().sort(null);
function DataSeries({
  data,
  centerValue,
  centerText,
  valueFormatter,
  smallCenterLabel,
  transform,
  labels,
  radius,
  innerRadius,
  colors,
  labelTextFill,
  valueTextFill,
  hideLabels,
  width
}) {
  const [offsets, setOffsets] = useState({});
  const containerRef = useRef();
  useEffect(() => {
    if (hideLabels) {
      return;
    }
    const container = containerRef.current;
    const parentRect = container.parentNode.getBoundingClientRect();
    const doesOverlap = (currentRect, previousRect) => parentRect.left - LABEL_MARGIN > currentRect.left || parentRect.right + LABEL_MARGIN < currentRect.right || previousRect && doesIntersect(currentRect, previousRect);
    const labelElements = [...container.querySelectorAll(".arc-label-group")];
    const rects = getRects(labelElements);
    const newOffsets = {};
    labelElements.forEach((element, index) => {
      const currentRect = rects[element.getAttribute("data-key")];
      const label = element.querySelector(".arc-label-text").getAttribute("data-label");
      const previousElements = labelElements.slice(0, index);
      previousElements.forEach((prevElement) => {
        const previousRect = rects[prevElement.getAttribute("data-key")];
        const doOverlap = doesOverlap(currentRect, previousRect);
        if (doOverlap) {
          newOffsets[label] = (offsets[label] ?? 0) + ADJUSTMENT_UNIT;
        }
      });
    });
    if (Object.entries(newOffsets).some(([key, value]) => offsets[key] !== value)) {
      setOffsets({
        ...offsets,
        ...newOffsets
      });
    }
  });
  return /* @__PURE__ */ jsxs("g", { ref: containerRef, className: "pie-group", transform, children: [
    getPieArcs(data).map((arc, i) => /* @__PURE__ */ jsx(
      Arc,
      {
        startAngle: arc.startAngle,
        endAngle: arc.endAngle,
        outerRadius: radius,
        innerRadius,
        labelTextFill,
        valueTextFill,
        fill: colors(i),
        label: labels[i],
        labelOffset: offsets[getIdentifierFromReactNode(labels[i])],
        hideLabels,
        value: data[i],
        valueFormatter,
        dataKey: i,
        width
      },
      i
    )),
    smallCenterLabel ? /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("text", { id: "pie-group-center-text", style: {
        textAnchor: "middle",
        fontSize: "11px",
        textTransform: "uppercase"
      }, y: "-5", children: centerText }),
      /* @__PURE__ */ jsx("text", { id: "pie-group-center-value", style: {
        textAnchor: "middle",
        fontSize: "20px",
        fontWeight: "bold"
      }, y: "20", children: valueFormatter(centerValue) })
    ] }) : /* @__PURE__ */ jsxs("g", { children: [
      /* @__PURE__ */ jsx("text", { style: {
        textAnchor: "middle",
        fontSize: "15px",
        fontWeight: "bold"
      }, y: "-10", children: centerText }),
      /* @__PURE__ */ jsx("text", { style: {
        textAnchor: "middle",
        fontSize: "28px"
      }, y: "20", children: valueFormatter(centerValue) })
    ] })
  ] });
}
DataSeries.propTypes = {
  centerText: PropTypes.string,
  colors: PropTypes.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  data: PropTypes.array,
  innerRadius: PropTypes.number,
  radius: PropTypes.number,
  transform: PropTypes.string,
  hideLabels: PropTypes.bool,
  smallCenterLabel: PropTypes.bool,
  centerValue: PropTypes.number,
  valueFormatter: PropTypes.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  labels: PropTypes.array,
  labelTextFill: PropTypes.string,
  valueTextFill: PropTypes.string,
  width: PropTypes.number
};
DataSeries.defaultProps = {
  innerRadius: 0,
  data: [],
  colors: scaleOrdinal(schemeCategory20)
};
export {
  DataSeries as default
};
